<script setup>
import { Marked } from 'marked';
import { gfmHeadingId } from 'marked-gfm-heading-id';
import { computed, useTemplateRef, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';

import { isInternalLink } from '~/common/LinkUtils';
import renderer from '~/pages/documentation-page/markedRenderer';

const container = useTemplateRef('container');
const router = useRouter();

const props = defineProps({
  markdownContent: {
    type: String,
    required: true,
  },
  useMarkedRenderer: {
    type: Boolean,
    required: false,
    default: false,
  },
});

function onClick(e) {
  const anchor = e.target?.closest?.('a');
  if (!anchor) {
    return;
  }

  const href = anchor.getAttribute('href');
  if (!isInternalLink(href)) {
    return;
  }

  const url = new URL(href, window.location.href);
  const target = url.pathname + url.search + url.hash;

  // Only intercept links that resolve to a Vue route.
  // Paths served by Rails (for example /start) must do a full
  // browser navigation so the server-side redirect can run.
  if (router.resolve(target).matched.length === 0) {
    return;
  }

  e.preventDefault();
  router.push(target);
}

const htmlContent = computed(() => {
  const marked = new Marked();

  if (props.useMarkedRenderer) {
    marked.use({ renderer });
  }

  marked.use(gfmHeadingId());

  return marked.parse(props.markdownContent);
});

onMounted(() => container.value?.addEventListener('click', onClick));
onBeforeUnmount(() => container.value?.removeEventListener('click', onClick));
</script>

<template>
  <div ref="container">
    <div
      v-safe-html="htmlContent"
      class="markdown-content"
    />
  </div>
</template>
